/**
 * Her corner of the diary.
 *
 * This replaces a bare `<p>` holding the greeting — which was the only place
 * she appeared in the everyday app, and which had two problems. The small one:
 * it rendered only when a display name was set, so an account without one got
 * no greeting at all, despite the lines being written to work without a name.
 * The large one: it was typography. There was nothing on the screen that was
 * *her*, so a sentence in her voice and a caption under a chart looked equally
 * like the app talking to itself.
 *
 * Now the mark stands next to what she says, in whatever colour today is, and
 * the whole strip can be tapped to ask her for more. Once a day the line
 * arrives word by word instead of simply being there — the same signature she
 * uses on the stage, spent exactly once so it keeps meaning "she is speaking"
 * rather than becoming how text renders around here.
 */

import { cn } from '@gramas/ui'
import { useEffect, useState } from 'react'
import { usePresence } from '../presence/store.js'
import { dailyLine, register } from '../sass/engine.js'
import { playCue } from '../sound/engine.js'
import { GhostReveal } from '../stage/GhostReveal.js'
import { herHour } from './clock.js'
import { takeFollowUpAsk } from './followups.js'
import { GlitimeMark } from './Mark.js'
import { petHer } from './pet.js'
import { claimDailyReveal } from './reveal.js'
import { TONE_TELL } from './tone.js'
import { openZone } from './zone.js'

export interface PresenceStripProps {
  /** Today's date, for pinning. */
  date: string
  userId: string
  /** Fallback for the register that has no greeting pool of its own. */
  fallback: string
  /**
   * Force or suppress the once-a-day arrival instead of claiming it from the
   * diary. Only the voice lab passes this: the reveal is spent on first sight
   * each morning, and a preview you could only see by waiting until tomorrow
   * is not a preview.
   */
  reveal?: boolean
}

export function PresenceStrip({ date, userId, fallback, reveal }: PresenceStripProps) {
  const presence = usePresence()

  // Claimed in a lazy initialiser rather than an effect: whether the line
  // arrives has to be known for the first paint, or the reveal starts a frame
  // late and reads as a stutter. The claim is idempotent per day, so a
  // re-render — or StrictMode's double invoke — cannot spend it twice. An
  // override skips the claim outright rather than overruling it, so previewing
  // the arrival cannot spend the real one.
  const [claimed] = useState(() => (reveal == null ? claimDailyReveal(userId, date) : false))
  const arriving = reveal ?? claimed

  // A due follow-up can become the day's greeting instead of the usual
  // catalogue draw — but only once. Decided in a lazy initialiser, same
  // trick as `claimed` above: it runs once at mount, not on every re-render,
  // so the greeting text this mounts with is the greeting it keeps for the
  // day. `takeFollowUpAsk` is the side effect (it marks the follow-up asked,
  // locally and server-side); if the chat opener already claimed it this
  // session, the shared cache and its guard mean this returns null here —
  // no explicit coordination between the two needed.
  const [followUpGreeting] = useState(() =>
    register() === 'sassy' ? (takeFollowUpAsk()?.ask ?? null) : null,
  )

  // The sound is a real side effect, so it waits for the commit.
  useEffect(() => {
    if (arriving) playCue('greet')
  }, [arriving])

  const line =
    followUpGreeting ??
    dailyLine('greeting', date, {
      hour: herHour(),
      mood: presence.mood,
      daysSinceLastLog: presence.daysSinceLastLog,
      herLife: presence.herLife,
    }) ??
    fallback

  // The legend for her colour. Fixed per tone rather than drawn — see `tone.ts`.
  const tell = TONE_TELL[presence.tone]

  return (
    <button
      type="button"
      onClick={() => openZone(date)}
      // Centred, not top-aligned. The greeting is one line some mornings and
      // two on others, and a mark pinned to the first line drifts up the
      // block as the sentence grows — she has to sit in the middle of what
      // she is saying, whatever length it turns out to be.
      className="-mx-1 flex w-full items-center gap-3 rounded-[var(--radius-card)] px-1 py-1 text-left transition-colors active:bg-raised"
    >
      {/* A tap on the strip opens her zone; a hold on the mark itself is a
          pet — the mark swallows the click a fired hold would leave behind,
          so the two gestures stay two gestures. */}
      <GlitimeMark size={38} live alive onPet={petHer} station="strip" />
      <span className="min-w-0 flex-1">
        <span className="block font-display font-semibold text-ink text-xl">
          {arriving ? <GhostReveal sweep>{line}</GhostReveal> : line}
        </span>
        {tell != null && (
          <span
            className={cn(
              'mt-0.5 block text-[0.6875rem] uppercase tracking-[0.14em]',
              'text-[var(--her)]',
            )}
          >
            {tell}
          </span>
        )}
      </span>
    </button>
  )
}
